import { useMemo, useState, type FormEvent } from "react";
import Header from "@/components/layout/header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { AlertCircle, Search, Trash2, UserX } from "lucide-react";
import {
  useCancelAccountErasure,
  useCreateGuestErasure,
  useGuestLookup,
  useSupportAccountErasures,
} from "@/hooks/useAccountDeletions";
import { formatDubaiDate } from "@/lib/dubaiDate";
import type { AccountErasureRequestResponse } from "@/types/generated/cms.generated";

/** Shape pre-check only — the API is the authority on whether the guest exists. */
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const PENDING_STATUSES = new Set(["Scheduled", "InProgress"]);

const requestedByLabel = (row: AccountErasureRequestResponse) =>
  row.trigger === "AthlynSupport" ? "Support" : "Self";

const statusLabel = (status: string | null | undefined) =>
  status === "InProgress" ? "In progress" : status ?? "";

/**
 * Account deletions — Athlyn support deletes a GUEST's account on an emailed request
 * (signed out now, erased within 30 days, cancellable until then). Employees are never deleted
 * here: their organisation's HR removes them. The lists show ids only (the API returns no email);
 * staff find a person by email with the lookup.
 */
export default function AccountDeletionsPage() {
  const [emailInput, setEmailInput] = useState("");
  const [submittedEmail, setSubmittedEmail] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [emailInvalid, setEmailInvalid] = useState(false);

  const lookup = useGuestLookup(submittedEmail);
  const erasures = useSupportAccountErasures();
  const createErasure = useCreateGuestErasure();
  const cancelErasure = useCancelAccountErasure();

  const { pending, completed } = useMemo(() => {
    const rows = erasures.data ?? [];
    return {
      pending: rows.filter((r) => PENDING_STATUSES.has(r.status)),
      completed: rows.filter((r) => r.status === "Completed"),
    };
  }, [erasures.data]);

  const onFind = (e: FormEvent) => {
    e.preventDefault();
    const email = emailInput.trim();
    if (!EMAIL_RE.test(email)) {
      setEmailInvalid(true);
      setSubmittedEmail("");
      return;
    }
    setEmailInvalid(false);
    // Same email again (e.g. after a failed lookup): the query key is unchanged, so refetch.
    if (email === submittedEmail) void lookup.refetch();
    else setSubmittedEmail(email);
  };

  const lookupError = lookup.error as { status?: number; errorCode?: string; message?: string } | null;
  const guest = lookup.data;
  const guestCanBeDeleted =
    !!guest && guest.status !== "Scheduled" && guest.status !== "InProgress" && guest.status !== "Completed";

  const onConfirmDelete = (e: React.MouseEvent) => {
    // Keep the dialog open (and its buttons disabled) until the request settles, so a
    // double-click can never send a second POST.
    e.preventDefault();
    if (!guest || createErasure.isPending) return;
    createErasure.mutate(
      { userId: guest.userId },
      { onSettled: () => setConfirmOpen(false) },
    );
  };

  return (
    <div className="flex-1" data-testid="account-deletions-page">
      <Header
        title="Account deletions"
        description="Delete a guest's account on their request. Employees are removed by their organisation's HR."
      />

      <div className="space-y-6 p-6">
        {/* Find a guest */}
        <Card data-testid="guest-lookup-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Search className="h-4 w-4" />
              Find a guest
            </CardTitle>
            <CardDescription>Search by the email address the guest signed up with.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <form className="flex flex-col gap-2 sm:flex-row sm:items-end" onSubmit={onFind} noValidate>
              <div className="flex-1 space-y-2">
                <Label htmlFor="guest-email">Guest email</Label>
                <Input
                  id="guest-email"
                  type="email"
                  value={emailInput}
                  onChange={(e) => {
                    setEmailInput(e.target.value);
                    if (emailInvalid) setEmailInvalid(false);
                  }}
                  placeholder="name@example.com"
                  aria-invalid={emailInvalid}
                  aria-describedby={emailInvalid ? "guest-email-error" : undefined}
                  data-testid="guest-lookup-email"
                />
              </div>
              <Button
                type="submit"
                disabled={emailInput.trim().length === 0 || lookup.isFetching}
                data-testid="guest-lookup-submit"
              >
                Find guest
              </Button>
            </form>
            {emailInvalid && (
              <p
                id="guest-email-error"
                className="text-sm text-destructive"
                data-testid="guest-lookup-invalid-email"
              >
                Enter a valid email address
              </p>
            )}

            {lookup.isFetching && <Skeleton className="h-20 w-full" />}

            {!lookup.isFetching && lookupError && lookupError.errorCode === "NOT_A_GUEST" && (
              <Alert data-testid="guest-lookup-not-guest">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Not a guest</AlertTitle>
                <AlertDescription>Employees are removed by their organisation&apos;s HR.</AlertDescription>
              </Alert>
            )}

            {!lookup.isFetching && lookupError && lookupError.status === 404 && (
              <Alert data-testid="guest-lookup-not-found">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>No guest account with that email</AlertDescription>
              </Alert>
            )}

            {!lookup.isFetching &&
              lookupError &&
              lookupError.errorCode !== "NOT_A_GUEST" &&
              lookupError.status !== 404 && (
                <Alert variant="destructive" data-testid="guest-lookup-error">
                  <AlertCircle className="h-4 w-4" />
                  <AlertTitle>Lookup failed</AlertTitle>
                  <AlertDescription>{lookupError.message || "Please try again."}</AlertDescription>
                </Alert>
              )}

            {!lookup.isFetching && guest && !lookupError && (
              <div
                className="flex flex-col gap-4 rounded-lg border p-4 sm:flex-row sm:items-start sm:justify-between"
                data-testid="guest-lookup-result"
              >
                <div className="space-y-1">
                  <p className="font-medium">{guest.name || "—"}</p>
                  <p className="text-sm text-muted-foreground">{guest.email}</p>
                  <p className="text-xs text-muted-foreground">User id: {guest.userId}</p>
                  {(guest.organisations ?? []).length > 0 && (
                    <div className="flex flex-wrap gap-1 pt-1">
                      {(guest.organisations ?? []).map((o) => (
                        <Badge key={o.organizationId} variant="outline">
                          {o.name}
                        </Badge>
                      ))}
                    </div>
                  )}
                  {guest.status && guest.status !== "None" && (
                    <p className="text-sm pt-1" data-testid="guest-lookup-status">
                      Deletion: {statusLabel(guest.status)}
                      {guest.eraseAfter && guest.status === "Scheduled"
                        ? ` — erased by ${formatDubaiDate(guest.eraseAfter)}`
                        : ""}
                    </p>
                  )}
                </div>
                {guestCanBeDeleted && (
                  <Button
                    variant="destructive"
                    onClick={() => setConfirmOpen(true)}
                    disabled={createErasure.isPending}
                    data-testid="guest-delete-button"
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    Delete account
                  </Button>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Pending deletions */}
        <Card data-testid="pending-deletions-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <UserX className="h-4 w-4" />
              Pending deletions
            </CardTitle>
            <CardDescription>
              Guests who asked to be deleted, or whom support deleted. Find a person by email above.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {erasures.isLoading ? (
              <Skeleton className="h-24 w-full" />
            ) : erasures.isError ? (
              <Alert variant="destructive" data-testid="deletions-load-error">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Could not load deletions</AlertTitle>
                <AlertDescription>
                  {(erasures.error as Error | null)?.message || "Please try again."}
                </AlertDescription>
              </Alert>
            ) : pending.length === 0 ? (
              <p className="text-sm text-muted-foreground" data-testid="pending-deletions-empty">
                No pending deletions.
              </p>
            ) : (
              <>
              {/* Small screens: stacked cards so Cancel is always visible */}
              <div className="space-y-3 sm:hidden">
                {pending.map((row) => (
                  <div key={row.id} className="rounded-lg border p-3 space-y-1" data-testid="pending-deletion-card">
                    <p className="font-mono text-xs break-all">{row.userId}</p>
                    <p className="text-sm">
                      <span className="text-muted-foreground">Requested on: </span>
                      {formatDubaiDate(row.requestedAt)}
                    </p>
                    <p className="text-sm">
                      <span className="text-muted-foreground">Erased by: </span>
                      {formatDubaiDate(row.eraseAfter)}
                    </p>
                    <p className="text-sm">
                      <span className="text-muted-foreground">Requested by: </span>
                      {requestedByLabel(row)}
                    </p>
                    <div className="flex items-center justify-between gap-2 pt-1">
                      <Badge variant={row.status === "InProgress" ? "secondary" : "outline"}>
                        {statusLabel(row.status)}
                      </Badge>
                      {row.status === "Scheduled" && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => cancelErasure.mutate(row.id)}
                          disabled={cancelErasure.isPending}
                          data-testid="pending-deletion-card-cancel"
                        >
                          Cancel
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              <div className="hidden overflow-x-auto sm:block">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>User id</TableHead>
                      <TableHead>Requested on</TableHead>
                      <TableHead>Erased by</TableHead>
                      <TableHead>Requested by</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pending.map((row) => (
                      <TableRow key={row.id} data-testid="pending-deletion-row">
                        <TableCell className="font-mono text-xs">{row.userId}</TableCell>
                        <TableCell>{formatDubaiDate(row.requestedAt)}</TableCell>
                        <TableCell>{formatDubaiDate(row.eraseAfter)}</TableCell>
                        <TableCell>{requestedByLabel(row)}</TableCell>
                        <TableCell>
                          <Badge variant={row.status === "InProgress" ? "secondary" : "outline"}>
                            {statusLabel(row.status)}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          {row.status === "Scheduled" && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => cancelErasure.mutate(row.id)}
                              disabled={cancelErasure.isPending}
                              data-testid="pending-deletion-cancel"
                            >
                              Cancel
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Completed deletions — ids only, no personal data remains */}
        <Card data-testid="completed-deletions-card">
          <CardHeader>
            <CardTitle>Completed deletions</CardTitle>
            <CardDescription>No personal data remains for these accounts.</CardDescription>
          </CardHeader>
          <CardContent>
            {erasures.isLoading ? (
              <Skeleton className="h-16 w-full" />
            ) : erasures.isError ? (
              <p className="text-sm text-destructive" data-testid="completed-deletions-load-error">
                Could not load deletions.
              </p>
            ) : completed.length === 0 ? (
              <p className="text-sm text-muted-foreground" data-testid="completed-deletions-empty">
                No completed deletions.
              </p>
            ) : (
              <>
              <div className="space-y-3 sm:hidden">
                {completed.map((row) => (
                  <div key={row.id} className="rounded-lg border p-3 space-y-1" data-testid="completed-deletion-card">
                    <p className="text-sm">
                      <span className="text-muted-foreground">Deleted on: </span>
                      {formatDubaiDate(row.completedAt ?? row.eraseAfter)}
                    </p>
                    <p className="font-mono text-xs break-all">User id: {row.userId}</p>
                    <p className="font-mono text-xs break-all">Request id: {row.id}</p>
                  </div>
                ))}
              </div>
              <div className="hidden overflow-x-auto sm:block">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Deleted on</TableHead>
                      <TableHead>User id</TableHead>
                      <TableHead>Request id</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {completed.map((row) => (
                      <TableRow key={row.id} data-testid="completed-deletion-row">
                        <TableCell>{formatDubaiDate(row.completedAt ?? row.eraseAfter)}</TableCell>
                        <TableCell className="font-mono text-xs">{row.userId}</TableCell>
                        <TableCell className="font-mono text-xs">{row.id}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      <AlertDialog
        open={confirmOpen}
        onOpenChange={(open) => {
          if (!createErasure.isPending) setConfirmOpen(open);
        }}
      >
        <AlertDialogContent data-testid="guest-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete account</AlertDialogTitle>
            <AlertDialogDescription>
              {`${guest?.email ?? ""} will be signed out now and permanently deleted within 30 days. You can cancel until then.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              disabled={createErasure.isPending}
              data-testid="guest-delete-dialog-cancel"
            >
              Keep account
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={onConfirmDelete}
              disabled={createErasure.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="guest-delete-confirm"
            >
              Delete account
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
